import Vue from 'vue'
import Router from 'vue-router'
import { routerConfig } from './router-config'
import store from '@/store'
import { parseEncryptedQuery, stringifyEncryptedQuery } from '@/utils/routeCrypto'

Vue.use(Router)

// 定义支持的语言列表和默认语言
export const supportedLanguages = ['en', 'cn', 'es', 'by', 'fr', 'de', 'it']
export const defaultLanguage = 'en'

// 语言验证函数
export const isValidLanguage = (lang) => supportedLanguages.includes(lang)

// 获取当前语言（优先级：localStorage > 默认语言）
export const getCurrentLanguage = () => {
  const stored = localStorage.getItem('language')
  if (stored) {
    try {
      const parsed = JSON.parse(stored)
      if (supportedLanguages.includes(parsed.country)) {
        return parsed.country
      }
    } catch {
      // 解析失败，使用默认语言
    }
  }
  return defaultLanguage
}

// 语言名称映射
const getLanguageName = (code) => {
  const names = {
    'en': 'English',
    'cn': '中文',
    'es': 'Español',
    'fr': 'Français',
    'by': 'Русский',
    'de': 'Deutsch',
    'it': 'italiano'
  }
  return names[code] || 'English'
}

// 基础路由配置（已包含 :lang 参数）
export const constantRoutes = routerConfig

// 创建路由实例
const routes = [
  // 根路径重定向到默认语言
  {
    path: '/',
    redirect: () => {
      const lang = getCurrentLanguage()
      return `/${lang}`
    }
  },
  // 主要路由（已经包含 :lang 参数）
  ...constantRoutes,
  // 404页面处理
  {
    path: '*',
    redirect: () => {
      const lang = getCurrentLanguage()
      return `/${lang}`
    }
  }
]

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  parseQuery: parseEncryptedQuery,
  stringifyQuery: stringifyEncryptedQuery,
  scrollBehavior: (to, from, savedPosition) => {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return { selector: to.hash }
    }
    return { x: 0, y: 0 }
  },
  routes
})

// 优化的路由守卫 - 彻底解决重定向循环问题
router.beforeEach(async (to, from, next) => {
  
  const currentLang = getCurrentLanguage()
  const routeLang = to.params.lang

  // 特殊处理：如果是根路径，直接重定向
  if (to.path === '/') {
    return next({
      path: `/${currentLang}`,
      replace: true
    })
  }

  // 如果路径没有语言参数且不是根路径，添加语言前缀
  if (!routeLang && !to.path.includes('/en/') && !to.path.includes('/cn/') && !to.path.includes('/es/') && !to.path.includes('/by/') && !to.path.includes('/fr/') && !to.path.includes('/de/') && !to.path.includes('/it/')) {
    const pathWithLang = `/${currentLang}${to.path}`
    return next({
      path: pathWithLang,
      query: to.query,
      hash: to.hash,
      replace: true
    })
  }

  // 验证语言参数有效性
  if (routeLang && !isValidLanguage(routeLang)) {
    const safePath = to.path.replace(`/${routeLang}`, `/${currentLang}`)
    return next({
      path: safePath,
      query: to.query,
      hash: to.hash,
      replace: true
    })
  }

  // 同步语言状态（仅当语言发生变化时）
  if (routeLang && routeLang !== store.state.user.lang) {
    try {
      const languageObj = { country: routeLang, name: getLanguageName(routeLang) }
      await store.dispatch('user/toggleLanguage', languageObj)
      
      // 同步到 vue-i18n（延迟执行，确保 Vue 实例已创建）
      Vue.nextTick(() => {
        if (router.app && router.app.$i18n) {
          router.app.$i18n.locale = routeLang
          document.documentElement.setAttribute('lang', routeLang)
        }
      })
    } catch (error) {
      console.error('Language sync failed:', error)
    }
  }

  // 对个人中心页面进行特殊处理，如果未登录则重定向到首页
  if (to.path.includes('/personalCenter') && !store.state.user.token) {
    return next({
      path: `/${currentLang}`,
      replace: true
    })
  }

  // 正常继续导航
  next()
})

export default router